import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import PaystackPop from "@paystack/inline-js";

const SHEETS_ENDPOINT =
  "https://script.google.com/macros/s/AKfycbx9M1F454r_BovQlWyCdUSHHxoM3Sm7EiaYYvplQSE4S00iRbUVphoh6z4dQNO2S0PG/exec";

const CONTACT_EMAIL = "contact@africaloudmag.com";
const TAB_ICON = "/favicon.ico";

const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "";
const PAYSTACK_CURRENCY = "ZAR";
const PAYSTACK_AMOUNT = 400000;
const PRICE_LABEL = "$250";
const PRODUCT_NAME =
  process.env.REACT_APP_PRODUCT_NAME || "Africa Loud Market Report";

const CONSENT_KEY = "africaloud_cookie_consent";

const signalCards = [
  {
    title: "Distribution is moving through smaller trusted networks",
    body:
      "In several African markets, product movement is shaped by local resellers, community referrals, and hybrid informal channels before larger formal systems fully absorb demand.",
    action:
      "Map trusted intermediaries first and structure your entry around how people already buy, ask, and refer.",
  },
  {
    title: "Payment behaviour follows access and timing, not only price",
    body:
      "Conversion is often influenced by cash flow timing, trust in seller relationships, and channel convenience as much as headline affordability.",
    action:
      "Test offers against payment timing, sales channel, and buyer confidence before finalising rollout assumptions.",
  },
];

const services = [
  {
    title: "Core Report",
    price: "$250",
    description:
      "Foundational view of market dynamics across key cities, including distribution systems, trust mechanisms, and transaction behaviour.",
    cta: "Access report",
    helper: "Immediate access to full market report",
    intent: "core-report",
    action: "paystack",
  },
  {
    title: "Sector Edition",
    price: "$700",
    description:
      "Applies the core market model to a specific industry, including distribution pathways, demand patterns, and structural gaps.",
    cta: "Request sector version",
    helper: "Prepared based on your industry focus",
    intent: "sector-edition",
    action: "contact",
  },
  {
    title: "Market Entry Strategy",
    price: "From $2,000",
    description:
      "Structured engagement covering entry sequencing, partner identification, pricing considerations, and operational constraints.",
    cta: "Discuss market entry",
    helper: "Structured around your target market and timeline",
    intent: "market-entry-strategy",
    action: "contact",
  },
];

const footerLinks = [
  { label: "Contact", href: "#contact" },
  { label: "Services", href: "#services" },
  { label: "Terms", href: "/terms" },
  { label: "Privacy", href: "/privacy" },
];

function getSavedConsent() {
  if (typeof window === "undefined") return "unknown";
  return localStorage.getItem(CONSENT_KEY) || "unknown";
}

function buildTrackingPayload(payload = {}) {
  return {
    timestamp: payload.timestamp || new Date().toISOString(),
    event_name: payload.event_name || "",
    page_path:
      payload.page_path ||
      (typeof window !== "undefined" ? window.location.pathname : ""),
    full_url:
      payload.full_url ||
      (typeof window !== "undefined" ? window.location.href : ""),
    page_title:
      payload.page_title ||
      (typeof document !== "undefined" ? document.title : "Africa Loud"),
    referrer:
      payload.referrer ||
      (typeof document !== "undefined"
        ? document.referrer || "direct"
        : "direct"),
    session_id: payload.session_id || "",
    consent_status: payload.consent_status || "",
    user_agent:
      payload.user_agent ||
      (typeof navigator !== "undefined" ? navigator.userAgent : ""),
    language:
      payload.language ||
      (typeof navigator !== "undefined" ? navigator.language : ""),
    screen_width:
      payload.screen_width ||
      (typeof window !== "undefined" ? String(window.screen.width) : ""),
    screen_height:
      payload.screen_height ||
      (typeof window !== "undefined" ? String(window.screen.height) : ""),
    viewport_width:
      payload.viewport_width ||
      (typeof window !== "undefined" ? String(window.innerWidth) : ""),
    viewport_height:
      payload.viewport_height ||
      (typeof window !== "undefined" ? String(window.innerHeight) : ""),
    timezone:
      payload.timezone ||
      (typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown"
        : "unknown"),
    platform:
      payload.platform ||
      (typeof navigator !== "undefined"
        ? navigator.platform || "unknown"
        : "unknown"),
    click_text: payload.click_text || "",
    click_id: payload.click_id || "",
    click_class: payload.click_class || "",
    scroll_percent: payload.scroll_percent || "",
    seconds_on_page: payload.seconds_on_page || "",
    extra:
      typeof payload.extra === "string"
        ? payload.extra
        : JSON.stringify(payload.extra || {}),
  };
}

function postToSheets(payload) {
  if (!SHEETS_ENDPOINT || typeof window === "undefined") return;

  const orderedPayload = buildTrackingPayload(payload);
  const body = new URLSearchParams();

  Object.entries(orderedPayload).forEach(([key, value]) => {
    body.append(key, value);
  });

  if (navigator.sendBeacon) {
    const blob = new Blob([body.toString()], {
      type: "application/x-www-form-urlencoded;charset=UTF-8",
    });
    navigator.sendBeacon(SHEETS_ENDPOINT, blob);
    return;
  }

  fetch(SHEETS_ENDPOINT, {
    method: "POST",
    mode: "no-cors",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body: body.toString(),
  }).catch(() => {});
}

function openPaystack({ email, name, company, onSuccess, onClose }) {
  if (!PAYSTACK_PUBLIC_KEY) {
    throw new Error("Missing Paystack public key");
  }

  const popup = new PaystackPop();

  popup.newTransaction({
    key: PAYSTACK_PUBLIC_KEY,
    email,
    amount: PAYSTACK_AMOUNT,
    currency: PAYSTACK_CURRENCY,
    ref: `al_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    metadata: {
      custom_fields: [
        {
          display_name: "Full name",
          variable_name: "full_name",
          value: name || "",
        },
        {
          display_name: "Company",
          variable_name: "company",
          value: company || "",
        },
        {
          display_name: "Product",
          variable_name: "product",
          value: PRODUCT_NAME,
        },
      ],
    },
    onSuccess,
    onCancel: onClose,
  });
}

function CookieConsentBanner({ visible, onAccept, onDecline }) {
  if (!visible) return null;

  return (
    <div className="fixed bottom-5 left-5 right-5 z-[9999]">
      <div className="max-w-5xl mx-auto bg-white text-black rounded-3xl shadow-2xl border border-gray-200 p-5 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.16em] text-[#c79a3b] mb-2">
              Cookies
            </p>
            <p className="text-sm md:text-base leading-7 text-gray-700">
              We use cookies and lightweight tracking to understand visits,
              page activity, clicks, and checkout behaviour. This helps us
              improve performance and measure demand across the site.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onDecline}
              className="px-5 py-3 rounded-2xl border border-gray-300 text-gray-700 font-medium"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={onAccept}
              className="px-5 py-3 rounded-2xl bg-[#0f2f63] text-white font-medium"
            >
              Accept cookies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AfricaLoudSuite() {
  const [statusMessage, setStatusMessage] = useState("");
  const [consentStatus, setConsentStatus] = useState(() => getSavedConsent());

  const [checkoutForm, setCheckoutForm] = useState({
    name: "",
    email: "",
    company: "",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "Sector Edition",
    market: "",
    notes: "",
  });

  const sessionId = useMemo(() => {
    if (typeof window === "undefined") return "";
    const existing = localStorage.getItem("ams_session_id");
    if (existing) return existing;
    const created = `ams-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    localStorage.setItem("ams_session_id", created);
    return created;
  }, []);

  const consentAccepted = consentStatus === "accepted";
  const showCookieBanner = consentStatus !== "accepted" && consentStatus !== "declined";

  useEffect(() => {
    if (!consentAccepted || !sessionId) return;

    postToSheets({
      event_name: "page_view",
      session_id: sessionId,
      consent_status: "accepted",
      extra: { source: "landing_page" },
    });
  }, [consentAccepted, sessionId]);

  useEffect(() => {
    if (!consentAccepted || !sessionId) return;

    const startedAt = Date.now();
    let maxScroll = 0;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrolled =
        docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

      if (scrolled > maxScroll) maxScroll = scrolled;
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);

      postToSheets({
        event_name: "session_summary",
        session_id: sessionId,
        consent_status: "accepted",
        scroll_percent: String(maxScroll),
        seconds_on_page: String(Math.round((Date.now() - startedAt) / 1000)),
        extra: { source: "session_cleanup" },
      });
    };
  }, [consentAccepted, sessionId]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateCheckout = (field, value) => {
    setCheckoutForm((current) => ({ ...current, [field]: value }));
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const trackIfConsented = (payload) => {
    if (!consentAccepted || !sessionId) return;
    postToSheets({
      ...payload,
      session_id: sessionId,
      consent_status: "accepted",
    });
  };

  const handleAcceptCookies = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setConsentStatus("accepted");

    postToSheets({
      event_name: "cookie_consent_accepted",
      session_id: sessionId,
      consent_status: "accepted",
      extra: { source: "cookie_banner" },
    });
  };

  const handleDeclineCookies = () => {
    localStorage.setItem(CONSENT_KEY, "declined");
    setConsentStatus("declined");
    setStatusMessage("Cookies declined. Tracking is turned off.");
  };

  const handlePaystackClick = (source = "report-access") => {
    if (!checkoutForm.name || !checkoutForm.email) {
      setStatusMessage("Add your name and email in the report access section first.");
      scrollToId("report-access");
      return;
    }

    trackIfConsented({
      event_name: "paystack_checkout_open",
      click_text: "Access report",
      click_id: `paystack-${source}`,
      click_class: "paystack-cta",
      extra: { source },
    });

    try {
      openPaystack({
        email: checkoutForm.email,
        name: checkoutForm.name,
        company: checkoutForm.company,
        onSuccess: (transaction) => {
          setStatusMessage(
            "Payment received. We will follow up using the email you entered."
          );

          trackIfConsented({
            event_name: "paystack_checkout_success",
            click_text: "Access report",
            click_id: transaction?.reference || "paystack-success",
            click_class: "paystack-cta",
            extra: {
              source,
              reference: transaction?.reference || "",
              email: checkoutForm.email,
              company: checkoutForm.company,
              currency: PAYSTACK_CURRENCY,
              amount: PAYSTACK_AMOUNT,
            },
          });
        },
        onClose: () => {
          setStatusMessage("Payment window closed before completion.");

          trackIfConsented({
            event_name: "paystack_checkout_cancelled",
            click_text: "Access report",
            click_id: `paystack-cancel-${source}`,
            click_class: "paystack-cta",
            extra: { source },
          });
        },
      });
    } catch (error) {
      setStatusMessage(
        "Paystack is not ready yet. Add your live public key to open payment."
      );
    }
  };

  const handleServiceClick = (service) => {
    if (service.action === "paystack") {
      trackIfConsented({
        event_name: "service_cta_click",
        click_text: service.cta,
        click_id: service.intent,
        click_class: "service-cta",
        extra: { service: service.title, intent: service.intent },
      });

      scrollToId("report-access");
      return;
    }

    updateForm("service", service.title);

    trackIfConsented({
      event_name: "service_cta_click",
      click_text: service.cta,
      click_id: service.intent,
      click_class: "service-cta",
      extra: { service: service.title, intent: service.intent },
    });

    scrollToId("contact");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const subject = `${form.service} enquiry from ${
      form.name || "Website visitor"
    }`;

    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Company: ${form.company}`,
      `Service: ${form.service}`,
      `Target market: ${form.market}`,
      `Notes: ${form.notes}`,
    ].join("\n");

    trackIfConsented({
      event_name: "lead_form_submit",
      click_text: "Send request",
      click_id: "lead-form-submit",
      click_class: "lead-form-submit",
      extra: {
        name: form.name,
        email: form.email,
        company: form.company,
        service: form.service,
        market: form.market,
        notes: form.notes,
      },
    });

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    setStatusMessage("Your email draft is ready.");
  };

  return (
    <>
      <Helmet>
        <title>Africa Loud | Market intelligence for African markets</title>
        <meta
          name="description"
          content="Market intelligence covering distribution systems, demand behaviour, and revenue pathways across Johannesburg, Lagos, and Nairobi."
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Manrope:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link rel="icon" type="image/x-icon" href={TAB_ICON} />
        <link rel="shortcut icon" href={TAB_ICON} />
        <link rel="apple-touch-icon" href={TAB_ICON} />
      </Helmet>

      <CookieConsentBanner
        visible={showCookieBanner}
        onAccept={handleAcceptCookies}
        onDecline={handleDeclineCookies}
      />

      <div
        className="min-h-screen bg-[#0f2f63] text-white"
        style={{ fontFamily: "Instrument Sans, sans-serif" }}
      >
        <header className="border-b border-white/10">
          <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-[#c79a3b]">
                Africa Loud
              </p>
              <p className="text-lg font-semibold text-white">AMS</p>
            </div>

            <nav className="flex flex-wrap gap-6 text-sm text-gray-300">
              <a href="#report-access" className="hover:text-white">
                Access report
              </a>
              <a href="#services" className="hover:text-white">
                Services
              </a>
              <a href="#contact" className="hover:text-white">
                Contact
              </a>
            </nav>
          </div>
        </header>

        <section className="px-6 py-20 max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <h1
              className="text-4xl md:text-5xl font-semibold leading-tight mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Market intelligence for African markets
            </h1>

            <p className="text-lg text-gray-300 mb-8 max-w-2xl leading-8">
              Analysis of distribution systems, demand behaviour, and revenue
              pathways across Johannesburg in South Africa, Lagos in Nigeria,
              and Nairobi in Kenya. Built for teams making market entry and
              growth decisions.
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-8">
              <div className="bg-[#c79a3b] text-black inline-block px-6 py-3 rounded-2xl font-medium">
                Core report {PRICE_LABEL}
              </div>

              <button
                type="button"
                onClick={() => scrollToId("report-access")}
                className="bg-white text-[#0f2f63] px-6 py-3 rounded-2xl font-medium"
              >
                Access report
              </button>
            </div>

            {statusMessage ? (
              <p className="text-sm text-gray-300">{statusMessage}</p>
            ) : null}
          </div>
        </section>

        <section className="bg-[#10284f] border-y border-white/10">
          <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10">
            {signalCards.map((signal) => (
              <div
                key={signal.title}
                className="bg-white/[0.04] rounded-3xl p-8 border border-white/10"
              >
                <p className="text-[#c79a3b] text-sm uppercase tracking-[0.16em] mb-4">
                  Signal
                </p>

                <h2
                  className="text-2xl font-semibold mb-4 leading-snug"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {signal.title}
                </h2>

                <p className="text-gray-300 leading-8 mb-6">{signal.body}</p>

                <div className="border-t border-white/10 pt-6">
                  <p className="text-sm uppercase tracking-[0.14em] text-[#c79a3b] mb-3">
                    Actionable step
                  </p>
                  <p className="text-gray-200 leading-7">{signal.action}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section
          id="report-access"
          className="bg-white text-black px-6 py-20 border-t border-b border-gray-200"
        >
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.16em] text-[#c79a3b] mb-4">
                Report access
              </p>

              <h2
                className="text-3xl font-semibold mb-6"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Access the core report
              </h2>

              
            </div>

            <div className="bg-[#f8f8f8] rounded-3xl p-8 border border-gray-200">
              <div className="grid md:grid-cols-1 gap-5">
                <label className="block">
                  <span className="text-sm text-gray-600">Name</span>
                  <input
                    type="text"
                    value={checkoutForm.name}
                    onChange={(e) => updateCheckout("name", e.target.value)}
                    placeholder="Your name"
                    className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63]"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-gray-600">Email</span>
                  <input
                    type="email"
                    value={checkoutForm.email}
                    onChange={(e) => updateCheckout("email", e.target.value)}
                    placeholder="Your email"
                    className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63]"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-gray-600">Company</span>
                  <input
                    type="text"
                    value={checkoutForm.company}
                    onChange={(e) => updateCheckout("company", e.target.value)}
                    placeholder="Company"
                    className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63]"
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={() => handlePaystackClick("report-access-section")}
                className="mt-6 bg-[#0f2f63] text-white px-6 py-3 rounded-2xl font-medium"
              >
                Access report
              </button>

              <p className="text-sm text-gray-500 mt-3">
                Checkout will charge 4,000 ZAR via Paystack. Approx. $250 USD
              </p>
            </div>
          </div>
        </section>

        <section id="services" className="bg-white text-black px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <h2
              className="text-3xl font-semibold mb-14"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Report and advisory structure
            </h2>

            <div className="grid lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="border border-gray-200 rounded-3xl p-8 flex flex-col"
                >
                  <p className="text-sm uppercase tracking-[0.14em] text-gray-500 mb-4">
                    {service.price}
                  </p>

                  <h3
                    className="text-2xl font-semibold mb-4"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {service.title}
                  </h3>

                  <p className="text-gray-700 leading-8 mb-8 flex-1">
                    {service.description}
                  </p>

                  <button
                    type="button"
                    onClick={() => handleServiceClick(service)}
                    className="bg-[#0f2f63] text-white px-5 py-3 rounded-2xl font-medium"
                  >
                    {service.cta}
                  </button>

                  <p className="text-sm text-gray-500 mt-3">{service.helper}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="px-6 py-20 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.16em] text-[#c79a3b] mb-4">
                Contact
              </p>

              <h2
                className="text-3xl font-semibold mb-6"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Request the report or discuss a sector specific brief
              </h2>

              <p className="text-gray-300 leading-8 max-w-xl">
                Share your focus area and the relevant service, and your request
                will open as a prepared email draft.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-white text-black rounded-3xl p-8 shadow-2xl"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-sm text-gray-600">Name</span>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => updateForm("name", e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63]"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-gray-600">Email</span>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm("email", e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63]"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-gray-600">Company</span>
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => updateForm("company", e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63]"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-gray-600">Service</span>
                  <select
                    value={form.service}
                    onChange={(e) => updateForm("service", e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63] bg-white"
                  >
                    {services
                      .filter((service) => service.action === "contact")
                      .map((service) => (
                        <option key={service.title} value={service.title}>
                          {service.title}
                        </option>
                      ))}
                  </select>
                </label>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <label className="block">
                  <span className="text-sm text-gray-600">Target market</span>
                  <input
                    type="text"
                    value={form.market}
                    onChange={(e) => updateForm("market", e.target.value)}
                    placeholder="Example: animal health in South Africa"
                    className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63]"
                  />
                </label>

                
              </div>

              <label className="block mt-5">
                <span className="text-sm text-gray-600">Notes</span>
                <textarea
                  rows={5}
                  value={form.notes}
                  onChange={(e) => updateForm("notes", e.target.value)}
                  placeholder="Tell us what market, sector, or decision you are evaluating."
                  className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-[#0f2f63] resize-none"
                />
              </label>

              <button
                type="submit"
                className="mt-6 bg-[#0f2f63] text-white px-6 py-3 rounded-2xl font-medium"
              >
                Send request
              </button>
            </form>
          </div>
        </section>

        <footer className="px-6 py-10 border-t border-white/10">
          <div className="max-w-6xl mx-auto flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-400">Africa Loud</p>

            <div className="flex flex-wrap gap-6 text-sm text-gray-400">
              {footerLinks.map((link) => (
                <a key={link.label} href={link.href} className="hover:text-white">
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}